import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const client = axios.create({ baseURL: API });

// Resolve a stored media url (e.g. "/api/media/xyz") to an absolute src.
export const mediaSrc = (u) => (u && u.startsWith("/api") ? `${BACKEND_URL}${u}` : u || "");

export function setAuthToken(token) {
  if (token) {
    client.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    delete client.defaults.headers.common.Authorization;
  }
}

export function formatApiError(err, fallback = "Something went wrong. Please try again.") {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const api = {
  // auth
  register: (data) => client.post("/auth/register", data).then((r) => r.data),
  login: (data) => client.post("/auth/login", data).then((r) => r.data),
  studentLogin: (data) => client.post("/auth/student-login", data).then((r) => r.data),
  me: () => client.get("/auth/me").then((r) => r.data),
  forgotPassword: (email) => client.post("/auth/forgot-password", { email }).then((r) => r.data),
  resetPassword: (token, new_password) => client.post("/auth/reset-password", { token, new_password }).then((r) => r.data),

  // students (teacher/parent/admin)
  listStudents: () => client.get("/students").then((r) => r.data),
  getClassCode: () => client.get("/students/class-code").then((r) => r.data),
  createStudent: (data) => client.post("/students", data).then((r) => r.data),

  // admin users
  listUsers: () => client.get("/users").then((r) => r.data),
  createUser: (data) => client.post("/users", data).then((r) => r.data),
  deleteUser: (id) => client.delete(`/users/${id}`).then((r) => r.data),

  // curriculum / learning
  getLetters: () => client.get("/letters").then((r) => r.data),
  getProgress: (studentId) => client.get(`/progress/${studentId}`).then((r) => r.data),
  createAttempt: (data) => client.post("/attempts", data).then((r) => r.data),
  teacherSummary: (studentId) => client.get(`/teacher/${studentId}`).then((r) => r.data),

  // curriculum (v2)
  getCurriculum: (studentId) =>
    client.get("/curriculum", { params: studentId ? { student_id: studentId } : {} }).then((r) => r.data),
  curriculumAttempt: (data) => client.post("/curriculum/attempts", data).then((r) => r.data),
  unlock: (data) => client.post("/curriculum/unlock", data).then((r) => r.data),
  search: (q) => client.get("/curriculum/search", { params: { q } }).then((r) => r.data),
  report: (studentId) => client.get(`/report/${studentId}`).then((r) => r.data),

  // assignments
  createAssignment: (data) => client.post("/assignments", data).then((r) => r.data),
  listAssignments: () => client.get("/assignments").then((r) => r.data),

  // certificates
  myCertificates: () => client.get("/certificates/mine").then((r) => r.data),
  studentCertificates: (studentId) => client.get(`/certificates/${studentId}`).then((r) => r.data),
  getMyCertPref: () => client.get("/certificates/settings/mine").then((r) => r.data),
  setMyCertPref: (enabled) => client.post("/certificates/settings/mine", { enabled }).then((r) => r.data),
  getPlatformCertPref: () => client.get("/admin/certificates/settings").then((r) => r.data),
  setPlatformCertPref: (enabled) => client.post("/admin/certificates/settings", { enabled }).then((r) => r.data),
  markCertificatePrinted: (cert_id) => client.post("/certificates/mark-printed", { cert_id }).then((r) => r.data),

  // writing samples
  saveSample: (data) => client.post("/writing-samples", data).then((r) => r.data),
  listSamples: (studentId) => client.get(`/writing-samples/${studentId}`).then((r) => r.data),
  reviewSample: (id, data) => client.patch(`/writing-samples/${id}/review`, data).then((r) => r.data),

  // students
  softDeleteStudent: (id) => client.delete(`/students/${id}`).then((r) => r.data),

  // admin curriculum
  adminCurriculum: () => client.get("/admin/curriculum").then((r) => r.data),
  updateActivity: (id, data) => client.patch(`/admin/activities/${id}`, data).then((r) => r.data),
  updateSettings: (data) => client.put("/admin/settings", data).then((r) => r.data),
  adminStats: () => client.get("/admin/stats").then((r) => r.data),

  // admin user management
  disableUser: (id) => client.post(`/users/${id}/disable`).then((r) => r.data),
  enableUser: (id) => client.post(`/users/${id}/enable`).then((r) => r.data),
  grantPlan: (id, data) => client.post(`/users/${id}/grant-plan`, data).then((r) => r.data),
  revokePlan: (id) => client.post(`/users/${id}/revoke-plan`).then((r) => r.data),

  // admin impersonation + bulk actions
  impersonate: (id) => client.post(`/admin/impersonate/${id}`).then((r) => r.data),
  bulkDeleteUsers: (user_ids) => client.post("/admin/users/bulk-delete", { user_ids }).then((r) => r.data),
  bulkPlanUsers: (data) => client.post("/admin/users/bulk-plan", data).then((r) => r.data),

  // admin lesson builder
  subjectsMeta: () => client.get("/admin/subjects-meta").then((r) => r.data),
  adminLessons: () => client.get("/admin/lessons").then((r) => r.data),
  activateFirstPerUnit: (body) =>
    client.post("/admin/lessons/activate-first-per-unit", body).then((r) => r.data),
  createLesson: (data) => client.post("/admin/lessons", data).then((r) => r.data),
  updateLesson: (id, data) => client.put(`/admin/lessons/${id}`, data).then((r) => r.data),
  deleteLesson: (id) => client.delete(`/admin/lessons/${id}`).then((r) => r.data),

  // lesson studio (admin + subscribed teacher/parent/reseller)
  studioMeta: () => client.get("/studio/meta").then((r) => r.data),
  myLessons: () => client.get("/my-lessons").then((r) => r.data),
  createMyLesson: (data) => client.post("/my-lessons", data).then((r) => r.data),
  updateMyLesson: (id, data) => client.put(`/my-lessons/${id}`, data).then((r) => r.data),
  deleteMyLesson: (id) => client.delete(`/my-lessons/${id}`).then((r) => r.data),
  aiGenerateLesson: (data) => client.post("/lessons/ai-generate", data).then((r) => r.data),
  aiGenerateStatus: (jobId) => client.get(`/lessons/ai-generate/${jobId}`).then((r) => r.data),
  aiGenerateCancel: (jobId) => client.post(`/lessons/ai-generate/${jobId}/cancel`).then((r) => r.data),
  differentiateLesson: (id) => client.post(`/lessons/${id}/differentiate`).then((r) => r.data),
  remediationDraft: (data) => client.post("/lessons/remediation-draft", data).then((r) => r.data),
  classInsights: () => client.get("/class-insights").then((r) => r.data),
  checkinToday: () => client.get("/checkins/today").then((r) => r.data),
  submitCheckin: (mood) => client.post("/checkins", { mood }).then((r) => r.data),
  aiGenerateTutorial: (data) => client.post("/lessons/ai-tutorial", data).then((r) => r.data),
  illustrate: (data) => client.post("/tutorial/illustrate", data).then((r) => r.data),
  adminCatalog: () => client.get("/admin/catalog").then((r) => r.data),
  platformToggleLesson: (lesson_id, enabled) => client.post("/admin/lessons/platform-toggle", { lesson_id, enabled }).then((r) => r.data),

  // lesson assignments (teacher/parent/admin -> student home)
  assignLesson: (data) => client.post("/lesson-assignments", data).then((r) => r.data),
  listLessonAssignments: () => client.get("/lesson-assignments").then((r) => r.data),
  deleteLessonAssignment: (id) => client.delete(`/lesson-assignments/${id}`).then((r) => r.data),

  // admin plans
  adminPlans: () => client.get("/admin/plans").then((r) => r.data),
  createPlan: (data) => client.post("/admin/plans", data).then((r) => r.data),
  updatePlan: (id, data) => client.put(`/admin/plans/${id}`, data).then((r) => r.data),
  togglePlan: (id, active) => client.post(`/admin/plans/${id}/toggle`, { active }).then((r) => r.data),
  deletePlan: (id) => client.delete(`/admin/plans/${id}`).then((r) => r.data),

  // admin settings (stripe keys)
  getStripeSettings: () => client.get("/admin/settings/stripe").then((r) => r.data),
  saveStripeSettings: (data) => client.post("/admin/settings/stripe", data).then((r) => r.data),
  adminPayments: () => client.get("/admin/payments").then((r) => r.data),
  adminPaymentDetail: (sessionId) => client.get(`/admin/payments/${sessionId}`).then((r) => r.data),
  adminCancelSubscription: (userId, immediate = false) => client.post(`/admin/subscription/${userId}/cancel`, { immediate }).then((r) => r.data),
  adminResumeSubscription: (userId) => client.post(`/admin/subscription/${userId}/resume`).then((r) => r.data),
  adminGetPaymentAlerts: () => client.get("/admin/payment-alerts").then((r) => r.data),
  adminSavePaymentAlerts: (payload) => client.post("/admin/payment-alerts", payload).then((r) => r.data),
  adminTestPaymentAlert: (to) => client.post("/admin/payment-alerts/test", { to }).then((r) => r.data),
  adminResolveFailure: (id, resolved = true, note = null) => client.post(`/admin/payment-failures/${id}/resolve`, { resolved, note }).then((r) => r.data),
  // Purchase Orders
  submitPurchaseOrder: (payload) => client.post("/purchase-orders", payload).then((r) => r.data),
  myPurchaseOrders: () => client.get("/purchase-orders/mine").then((r) => r.data),
  adminListPurchaseOrders: (status) => client.get("/admin/purchase-orders", { params: status ? { status } : {} }).then((r) => r.data),
  adminInvoicePO: (id, payload) => client.post(`/admin/purchase-orders/${id}/invoice`, payload || {}).then((r) => r.data),
  adminMarkPOPaid: (id, payload) => client.post(`/admin/purchase-orders/${id}/mark-paid`, payload || {}).then((r) => r.data),
  adminCancelPO: (id, payload) => client.post(`/admin/purchase-orders/${id}/cancel`, payload || {}).then((r) => r.data),

  // lesson library (teacher/parent/reseller)
  getLibrary: () => client.get("/library").then((r) => r.data),  toggleLibrary: (lesson_id, enabled) => client.post("/library/toggle", { lesson_id, enabled }).then((r) => r.data),
  toggleLibrarySubject: (subject_id, enabled) => client.post("/library/toggle-subject", { subject_id, enabled }).then((r) => r.data),
  onboardingStatus: () => client.get("/onboarding").then((r) => r.data),

  // universal PreK-8 subjects
  listSubjects: (studentId) =>
    client.get("/subjects", { params: studentId ? { student_id: studentId } : {} }).then((r) => r.data),
  getSubject: (subjectId, studentId) =>
    client.get(`/subjects/${subjectId}`, { params: studentId ? { student_id: studentId } : {} }).then((r) => r.data),
  getLesson: (lessonId, studentId) =>
    client.get(`/lessons/${lessonId}`, { params: studentId ? { student_id: studentId } : {} }).then((r) => r.data),
  subjectAttempt: (data) => client.post("/subjects/attempts", data).then((r) => r.data),

  // stickers, family progress, subscription
  getStickers: (studentId) => client.get(`/stickers/${studentId}`).then((r) => r.data),
  familyProgress: (studentId) => client.get(`/family-progress/${studentId}`).then((r) => r.data),
  mySubscription: () => client.get("/subscription/me").then((r) => r.data),

  // payments (Stripe)
  getPlans: () => client.get("/payments/plans").then((r) => r.data),
  checkout: (data) => client.post("/payments/checkout", data).then((r) => r.data),
  paymentStatus: (sessionId) => client.get(`/payments/status/${sessionId}`).then((r) => r.data),

  // subscription management
  mySub: () => client.get("/subscription/me").then((r) => r.data),
  changeSub: (lookup_key) => client.post("/subscription/change", { lookup_key }).then((r) => r.data),
  cancelSub: () => client.post("/subscription/cancel").then((r) => r.data),
  resumeSub: () => client.post("/subscription/resume").then((r) => r.data),

  // reseller white-label
  getResellerBrand: () => client.get("/reseller/brand").then((r) => r.data),
  saveResellerBrand: (data) => client.put("/reseller/brand", data).then((r) => r.data),
  getResellerCodes: () => client.get("/reseller/codes").then((r) => r.data),
  genResellerCodes: (count) => client.post("/reseller/codes", { count }).then((r) => r.data),
  revokeResellerCode: (code) => client.delete(`/reseller/codes/${code}`).then((r) => r.data),
  getResellerClients: () => client.get("/reseller/clients").then((r) => r.data),
  toggleResellerCode: (code) => client.post(`/reseller/codes/${code}/toggle`).then((r) => r.data),
  bulkToggleResellerCodes: (codes, status) => client.post("/reseller/codes/bulk-toggle", { codes, status }).then((r) => r.data),

  // Admin — reseller brand slug hygiene
  adminListResellerSlugs: () => client.get("/admin/reseller-slugs").then((r) => r.data),
  adminPurgeResellerSlugs: () => client.post("/admin/reseller-slugs/purge").then((r) => r.data),
  adminDeleteResellerSlug: (slug) => client.delete(`/admin/reseller-slugs/${slug}`).then((r) => r.data),

  // Reseller Stripe Connect + reseller-defined plans
  resellerStripeStatus: () => client.get("/reseller/stripe/status").then((r) => r.data),
  resellerStripeConnectLink: () => client.post("/reseller/stripe/connect-link").then((r) => r.data),
  resellerStripeDisconnect: () => client.post("/reseller/stripe/disconnect").then((r) => r.data),
  listResellerPlans: () => client.get("/reseller/plans").then((r) => r.data),
  createResellerPlan: (data) => client.post("/reseller/plans", data).then((r) => r.data),
  updateResellerPlan: (id, data) => client.patch(`/reseller/plans/${id}`, data).then((r) => r.data),
  deleteResellerPlan: (id) => client.delete(`/reseller/plans/${id}`).then((r) => r.data),
  publicResellerPlans: (slug) => client.get(`/public/reseller/${slug}/plans`).then((r) => r.data),
  startResellerPlanCheckout: (slug, plan_id, origin_url) =>
    client.post(`/public/reseller/${slug}/plans/${plan_id}/checkout`, { origin_url }).then((r) => r.data),
  resellerPlanStatus: (session_id) =>
    client.get(`/reseller-plan/status/${session_id}`).then((r) => r.data),
  resellerPlansAnalytics: () => client.get("/reseller/plans/analytics").then((r) => r.data),

  // Client-facing "My Plan" portal for those who subscribed via a reseller plan
  myResellerPlan: () => client.get("/reseller-plan/mine").then((r) => r.data),
  cancelMyResellerPlan: () => client.post("/reseller-plan/cancel").then((r) => r.data),
  resumeMyResellerPlan: () => client.post("/reseller-plan/resume").then((r) => r.data),

  // schools & districts (org)
  getMySchool: () => client.get("/org/my-school").then((r) => r.data),
  createSchool: (name) => client.post("/org/school", { name }).then((r) => r.data),
  getMyDistrict: () => client.get("/org/my-district").then((r) => r.data),
  getDistrictRollup: () => client.get("/org/district/rollup").then((r) => r.data),
  createDistrict: (name) => client.post("/org/district", { name }).then((r) => r.data),
  addDistrictSchool: (name, seats) => client.post("/org/district/schools", { name, seats }).then((r) => r.data),
  setSchoolSeats: (id, seats) => client.put(`/org/district/schools/${id}/seats`, { seats }).then((r) => r.data),
  createInvite: (data) => client.post("/org/invites", { base_url: window.location.origin, ...data }).then((r) => r.data),
  bulkInviteTeachers: (emails) => client.post("/org/invites/bulk", { base_url: window.location.origin, emails }).then((r) => r.data),
  revokeInvite: (id) => client.delete(`/org/invites/${id}`).then((r) => r.data),
  getInviteInfo: (token) => client.get(`/org/invites/${token}`).then((r) => r.data),
  acceptInvite: (data) => client.post("/org/invites/accept", data).then((r) => r.data),
  getSmtp: () => client.get("/org/smtp").then((r) => r.data),
  setSmtp: (data) => client.post("/org/smtp", data).then((r) => r.data),
  setStudentAutoplay: (id, enabled) => client.post(`/students/${id}/settings`, { autoplay: enabled }).then((r) => r.data),
  brandBySlug: (slug) => client.get(`/brand/slug/${slug}`).then((r) => r.data),
  brandMe: () => client.get("/brand/me").then((r) => r.data),

  // ---- TTS & voice notes ----
  ttsVoices: () => client.get("/tts/voices").then((r) => r.data),
  ttsPreview: (payload) =>
    client.post("/tts-preview", payload, { responseType: "blob" }).then((r) => r.data),
  uploadVoiceNote: (blob, filename = "recording.webm") => {
    const fd = new FormData();
    fd.append("file", blob, filename);
    return client.post("/voice-notes", fd, { headers: { "Content-Type": "multipart/form-data" } })
      .then((r) => r.data);
  },
  fetchVoiceNoteBlob: (noteId) =>
    client.get(`/voice-notes/${noteId}`, { responseType: "blob" }).then((r) => r.data),
  deleteVoiceNote: (noteId) =>
    client.delete(`/voice-notes/${noteId}`).then((r) => r.data),

  // ---- AI Keys BYOK (admin) ----
  getAIKeys: () => client.get("/admin/settings/ai-keys").then((r) => r.data),
  saveAIKeys: (data) => client.post("/admin/settings/ai-keys", data).then((r) => r.data),

  // ---- Bulk lesson upload (admin) ----
  bulkParseLessons: (body) =>
    client.post("/admin/lessons/bulk-upload/parse", body).then((r) => r.data),
  bulkRunLessons: (body) =>
    client.post("/admin/lessons/bulk-upload/run", body).then((r) => r.data),
  bulkJobStatus: (jobId) =>
    client.get(`/admin/lessons/bulk-upload/jobs/${jobId}`).then((r) => r.data),
  bulkRecentJobs: () =>
    client.get("/admin/lessons/bulk-upload/jobs").then((r) => r.data),
  bulkJobUndo: (jobId) =>
    client.post(`/admin/lessons/bulk-upload/jobs/${jobId}/undo`).then((r) => r.data),
  bulkJobCancel: (jobId) =>
    client.post(`/admin/lessons/bulk-upload/jobs/${jobId}/cancel`).then((r) => r.data),
  bulkReapStale: () =>
    client.post("/admin/lessons/bulk-upload/reap-stale").then((r) => r.data),

  // ---- Bulk upload prompt-library presets ----
  listBulkPresets: () =>
    client.get("/admin/lessons/bulk-upload/presets").then((r) => r.data),
  saveBulkPreset: (body) =>
    client.post("/admin/lessons/bulk-upload/presets", body).then((r) => r.data),
  deleteBulkPreset: (id) =>
    client.delete(`/admin/lessons/bulk-upload/presets/${id}`).then((r) => r.data),

  // ---- Clone built-in lesson into an editable custom copy (admin) ----
  cloneBuiltinLesson: (lessonId) =>
    client.post(`/admin/lessons/clone-builtin/${lessonId}`).then((r) => r.data),
  revertForkedLesson: (lessonId) =>
    client.post(`/admin/lessons/${lessonId}/revert-fork`).then((r) => r.data),

  // ---- Bulk update on my lessons ----
  bulkUpdateMyLessons: (body) =>
    client.post("/my-lessons/bulk-update", body).then((r) => r.data),
};

export const ttsUrl = (text, voice = "nova", speed = 1) =>
  `${API}/tts?text=${encodeURIComponent(text)}&voice=${voice}${speed && speed !== 1 ? `&speed=${speed}` : ""}`;

